import { Button, Divider, Paper, Typography, Collapse, Box as MuiBox } from "@mui/material";
import CommentIcon from '@mui/icons-material/Comment';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box } from "@mui/system";
import React, { useState, useEffect } from "react"
import { Loader } from "../Loader";
import { useTranslation } from "react-i18next";
import { Matrix } from "./PlotTypes/Matrix";
import { Radiogroup } from "./PlotTypes/radiogroup";
import { TableChart } from "./PlotTypes/TableChart";
import { Checkbox } from "./PlotTypes/checkbox";
import { Dropdown } from "./PlotTypes/dropdown";

const PLOT_TYPES = {
  matrix: Matrix,
  radiogroup: Radiogroup,
  table: TableChart,
  checkbox: Checkbox,
  dropdown: Dropdown
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 2, textAlign: 'center', color: 'error.main' }}>
          Klaida apdorojant grafiką
        </Box>
      );
    }

    return this.props.children;
  }
}

  const Plot = React.memo(({ klausimas = {}, atsakymai = [] }) => {
    const processedAtsakymai = atsakymai;
  const { t } = useTranslation();
  
  // Group all state hooks
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showComments, setShowComments] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Memoize comments once
  const comments = React.useMemo(() => 
    atsakymai.map(item => item[`${klausimas.name}-Comment`]).filter(Boolean),
    [atsakymai, klausimas.name]
  );

  // Handle loading and error states
  useEffect(() => {
    setIsLoading(true);
    setError(null);
    
    const validateData = () => {
      if (!klausimas || typeof klausimas !== 'object') {
        throw new Error('Neteisingi klausimo duomenys');
      }

      const tipas = klausimas?.type;
      if (!tipas || !PLOT_TYPES[tipas]) {
        throw new Error(`Nepalaikomas grafiko tipas: ${tipas || 'undefined'}`);
      }

      if (!atsakymai.length) {
        throw new Error('Nėra atsakymų');
      }
    };

    try {
      validateData();
      setError(null);
    } catch (err) {
      setError(err.message);
      if (retryCount < 3) {
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          setIsLoading(true);
        }, 500);
        return;
      }
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 100);
    }
  }, [klausimas, atsakymai, retryCount]);

  if (isLoading) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <Loader />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', color: 'error.main' }}>
        {error}
      </Box>
    );
  }

  const tipas = klausimas.type;
  const PlotComponent = PLOT_TYPES[tipas];

  // Event handlers after hooks
  const toggleShowComments = () => {
    setShowComments(!showComments);
  }

  return (
    <Box pb={2} className="plot-wrapper">
      <ErrorBoundary>
        <Box className="plot-container" sx={{ opacity: 1, transition: 'opacity 0.3s ease-in-out' }}>
          <PlotComponent 
            klausimas={klausimas} 
            atsakymai={processedAtsakymai}
          />
        </Box>
      </ErrorBoundary>
      {comments.length > 0 && (
        <MuiBox sx={{ mt: 3, px: 3 }}>
          <Divider sx={{ mb: 3 }} />
          <Button
            onClick={toggleShowComments}
            startIcon={<CommentIcon sx={{ fontSize: 20 }} />}
            endIcon={showComments ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            sx={{
              textTransform: 'none',
              color: 'text.primary',
              mb: 2.5,
              fontSize: '0.9375rem',
              fontWeight: 500,
              py: 1,
              px: 2,
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)'
              }
            }}
          >
            {t('grafikaikomentaras')} ({comments.length})
          </Button>
          <Collapse in={showComments}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 0,
                backgroundColor: 'transparent',
                borderRadius: 2
              }}
            >
              {comments.map((comment, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 2,
                    backgroundColor: '#f8f9fa',
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: 'rgba(0, 0, 0, 0.08)',
                    '&:last-child': {
                      mb: 0
                    }
                  }}
                >
                  <Typography
                    sx={{
                      color: 'text.primary',
                      fontSize: '0.9375rem',
                      lineHeight: 1.7,
                      letterSpacing: '0.01em',
                      whiteSpace: 'pre-line'
                    }}
                  >
                    {comment}
                  </Typography>
                </Paper>
              ))}
            </Paper>
          </Collapse>
        </MuiBox>
      )}
    </Box>
  )
});

export default Plot;
